import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { FaArrowLeft, FaEyeSlash, FaEye, FaUserCog, FaExchangeAlt } from 'react-icons/fa';
import LoadingSpinner from '../common/LoadingSpinner';
import TreeChart from '../TrendRadarChart/components/TreeChart';
import ZoomControls from '../TrendRadarChart/components/ZoomControls';
import SearchBar from '../TrendRadarChart/components/SearchBar';
import Breadcrumb from '../TrendRadarChart/components/Breadcrumb';
import NavigationPanel from '../TrendRadarChart/components/NavigationPanel';
import TagSelectorModalComponent from '../common/TagSelectorModal';
import SkillHistoryModal from '../TrendRadarChart/components/TreeChart/components/SkillHistoryModal';
import useSearch from '../TrendRadarChart/hooks/useSearch';
import useChartData from '../TrendRadarChart/hooks/useChartData';
import useZoomPan from '../TrendRadarChart/hooks/useZoomPan';
import useTooltip from '../TrendRadarChart/hooks/useTooltip';
import { useRadarData } from '../../hooks/useRadarData';
import { useAlert } from '../../contexts/AlertContext';
import { TREE_CHART_MODES, APPROVAL_STAGES } from '../../constants/radar';
import { tagsApi, employeesApi, competencyTemplatesApi } from '../../services/api';
import {
  Container,
  Header,
  HeaderLeft,
  BackButton,
  Title,
  Content,
  ChartPanel,
  HeaderButton,
} from './styles';

import { SEGMENT_TYPE } from '../../constants/radar';
import { SKILL_TYPES } from '../../constants/skillTypes';
import { getDefaultLevelId } from '../../constants/skillLevels';
const EmployeeConfiguration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { employeeId } = useParams();
  const { showAlert } = useAlert();
  const [employeeUser, setEmployeeUser] = useState(location.state?.user || null);
  const fromLandingPage = location.state?.fromLandingPage || false;
  // ADR-109 §3 — set when opened from an Individual page; restores that page and its return state.
  const individualReturn = location.state?.individualReturn || null;
  const { data: radarData, loading: radarLoading, error: radarError } = useRadarData();

  // Fetch employee data if we have an ID but no user in state
  useEffect(() => {
    const fetchEmployeeUser = async () => {
      if (employeeId && !employeeUser) {
        try {
          const employee = await employeesApi.getEmployeeById(employeeId);
          if (employee) {
            setEmployeeUser({
              id: employee.id,
              name: employee.fullName,
              employee: employee
            });
          }
        } catch (err) {
          console.error('Failed to fetch employee:', err);
          showAlert('Failed to load employee data', 'error');
        }
      }
    };
    fetchEmployeeUser();
  }, [employeeId, employeeUser, showAlert]);

  // State
  const [employeeData, setEmployeeData] = useState(null);
  const [employeeHierarchyData, setEmployeeHierarchyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPanelsVisible, setIsPanelsVisible] = useState(true);
  const [isTreeExpanded, setIsTreeExpanded] = useState(false);
  const [expandedPath, setExpandedPath] = useState([]);
  const [isNavigationPanelCollapsed, setIsNavigationPanelCollapsed] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [isTagsLoading, setIsTagsLoading] = useState(false);
  const [isTagSelectorOpen, setIsTagSelectorOpen] = useState(false);
  const [initialCheckedLeafs, setInitialCheckedLeafs] = useState(new Set());
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const [selectedSkillHistory, setSelectedSkillHistory] = useState(null);
  const [competencyLevels, setCompetencyLevels] = useState([]);
  
  // Check if a leaf (regular or custom) is assigned to the employee
  const isLeafAssigned = useCallback((leafId) => {
    if (!employeeData?.employeeLeafSegments) {
      return false;
    }
    return employeeData.employeeLeafSegments.some(
      (segment) => segment.leafSegment?.segmentId === leafId || segment.customSegment?.id === leafId
    );
  }, [employeeData]);

  // Filter data by tags
  const filterDataByTags = useCallback((data, selectedTags) => {
    if (!selectedTags.length) return data;

    const filterNodes = (node) => {
      if (!node) return null;

      if (node.segmentType === SEGMENT_TYPE.LEAF) {
        const hasSelectedTag = selectedTags.some(selectedTag => 
          node.tags?.some(nodeTag => 
            nodeTag.id === selectedTag.id || 
            nodeTag.parentTagId === selectedTag.id
          )
        );

        return hasSelectedTag ? node : null;
      }

      const filteredSubcategories = (node.subcategories || [])
        .map(child => filterNodes(child))
        .filter(Boolean);

      return filteredSubcategories.length > 0
        ? { ...node, subcategories: filteredSubcategories }
        : null;
    };

    return filterNodes(data);
  }, []);

  // Helper function to count nodes in tree
  const countNodes = (node) => {
    if (!node) return 0;
    const subcategoriesCount = (node.subcategories || [])
      .reduce((sum, child) => sum + countNodes(child), 0);
    return 1 + subcategoriesCount;
  };

  // Get filtered data
  const getFilteredData = useCallback(() => {
    let filteredData = employeeHierarchyData;
    if (!filteredData) return null;

    // First apply selection filter if needed
    if (showOnlySelected) {
      const filterBySelection = (node) => {
        if (!node) return null;

        if (!node.subcategories || node.subcategories.length === 0) {
          return initialCheckedLeafs.has(node.leafSegmentId) ? node : null;
        }

        const filteredSubcategories = (node.subcategories || [])
          .map(filterBySelection)
          .filter(Boolean);

        return filteredSubcategories.length > 0
          ? { ...node, subcategories: filteredSubcategories }
          : null;
      };

      const selectionFiltered = filterBySelection(filteredData);
      // If no nodes match selection criteria, return empty root node
      filteredData = selectionFiltered || {
        ...filteredData,
        subcategories: []
      };
    }

    // Then apply tag filter
    const tagFiltered = filterDataByTags(filteredData, selectedTags);
    
    // If no nodes match tag criteria, return empty root node
    return tagFiltered || {
      ...filteredData,
      subcategories: []
    };
  }, [employeeHierarchyData, showOnlySelected, initialCheckedLeafs, selectedTags, filterDataByTags]);

  // Set initial radar data
  useEffect(() => {
    if (radarData) {
      // Add skill levels to leaf nodes
      const enrichDataWithSkillLevels = (data) => {
        if (!data) return null;

        const enrichNode = (node) => {
          if (node.segmentType === SEGMENT_TYPE.LEAF) {
            // Find the employee leaf segment for this node
            const employeeLeafSegment = employeeData?.employeeLeafSegments?.find(
              segment => segment.leafSegment?.id === node.leafSegmentId
            );
            // Add skillLevel and skillHistory properties if the leaf is assigned to the employee
            // Use competencyLevelId directly or from competencyLevel object
            return {
              ...node,
              competencyLevelId: employeeLeafSegment?.competencyLevelId || employeeLeafSegment?.competencyLevel?.id || getDefaultLevelId(competencyLevels),
              skillHistory: employeeLeafSegment?.skillHistory || null,
              currentSkill: employeeLeafSegment?.currentSkill || null
            };
          }

          // If it's not a leaf node, process its subcategories
          if (node.subcategories?.length > 0) {
            return {
              ...node,
              subcategories: node.subcategories.map(enrichNode)
            };
          }

          return node;
        };

        return enrichNode(data);
      };

      // Inject custom segments into the tree under "Custom Technologies" category
      const injectCustomSegments = (treeData) => {
        // Find all custom segments (where leafSegment is null but customSegment exists)
        const customSegments = employeeData?.employeeLeafSegments?.filter(
          s => s.leafSegment == null && s.customSegment != null
        ) || [];

        if (customSegments.length === 0) {
          return treeData;
        }

        // Create "Custom Technologies" category node (first-level INTERNAL node)
        const customTechNode = {
          id: 'custom-technologies',
          name: 'Custom Technologies',
          segmentType: SEGMENT_TYPE.INTERNAL,
          internalSegmentId: 'custom-technologies',
          color: '#9C27B0',  // Purple color for custom category
          subcategories: [],
          segmentProcesses: []
        };

        // Create leaf nodes for each custom segment, sorted by name
        const sortedCustomSegments = [...customSegments].sort((a, b) =>
          (a.customSegment.name || '').localeCompare(b.customSegment.name || '')
        );

        sortedCustomSegments.forEach((segment) => {
          const customLeafNode = {
            id: segment.customSegment.id,
            name: segment.customSegment.name,
            segmentType: SEGMENT_TYPE.LEAF,
            leafSegmentId: segment.customSegment.id,
            isCustomSegment: true,  // Flag to identify custom segments
            tags: [],
            approvalStage: APPROVAL_STAGES.HOLD,
            projects: [],
            subcategories: [],
            // Enrich with skill data - use competencyLevelId directly or from competencyLevel object
            competencyLevelId: segment.competencyLevelId || segment.competencyLevel?.id || getDefaultLevelId(competencyLevels),
            skillHistory: segment.skillHistory || null,
            currentSkill: segment.currentSkill || null
          };

          customTechNode.subcategories.push(customLeafNode);
        });

        // Inject as first-level category in original tree
        return {
          ...treeData,
          subcategories: [...(treeData.subcategories || []), customTechNode]
        };
      };

      const enrichedData = enrichDataWithSkillLevels(radarData);
      const dataWithCustomSegments = injectCustomSegments(enrichedData);
      setEmployeeHierarchyData(dataWithCustomSegments);
    }
  }, [radarData, employeeData, competencyLevels]);

  // Fetch competency templates
  useEffect(() => {
    const fetchCompetencyTemplates = async () => {
      try {
        // Fetch technical competency templates
        const templates = await competencyTemplatesApi.getTemplatesByType(SKILL_TYPES.TECHNICAL);
        
        // If templates exist and have levels, transform the first template's levels
        if (templates && templates.length > 0 && templates[0].levels) {
          const transformedLevels = templates[0].levels.map(level => ({
            level: level.level,
            displayName: level.name,
            color: level.color,
            defaultDescription: level.description,
            description: level.description,
            levelId: level.id
          }));
          
          setCompetencyLevels(transformedLevels);
        }
      } catch (error) {
        console.error('Failed to fetch competency templates:', error);
        showAlert('Failed to fetch competency templates', 'error', error);
      }
    };

    fetchCompetencyTemplates();
  }, [showAlert]);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!employeeUser?.id) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const employeeDetails = await employeesApi.getEmployeeById(employeeUser.id);
        setEmployeeData(employeeDetails);
        
        // Set initial checked leafs based on employee's assigned segments (including custom segments)
        const radarLeafs = employeeDetails.employeeLeafSegments
          ?.filter((leafSegment) => leafSegment.leafSegment != null)
          ?.map((leafSegment) => leafSegment.leafSegment.id) || [];

        const customLeafs = employeeDetails.employeeLeafSegments
          ?.filter((s) => s.leafSegment == null && s.customSegment != null)
          ?.map((s) => s.customSegment.id) || [];

        const initialLeafs = new Set([...radarLeafs, ...customLeafs]);
        setInitialCheckedLeafs(initialLeafs);
      } catch (error) {
        console.error('Failed to fetch employee data:', error);
        setError(error.message || 'Failed to fetch individual data');
        showAlert('Failed to fetch individual data', 'error', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, [employeeUser, showAlert]);

  // Handle leaf toggle (assign/unassign skill)
  const handleLeafToggle = async (leafId, isChecked) => {
    if (!employeeUser) return;

    // In employee mode, clicking the checkbox should open the skill level selector
    // We're always in employee mode in this component
    return;
  };

  // Update handleSkillLevelChange to use the API values directly
  // options.skipCompetencyLevel - when true, adds/removes skill without competency level (for disabled competency assignment)
  // options.isCustomSegment - when true, sends customSegmentId instead of leafSegmentId
  const handleSkillLevelChange = async (leafId, competencyLevelId, options = {}) => {
    if (!employeeUser) {
      return;
    }

    const { skipCompetencyLevel = false, isCustomSegment = false } = options;

    // Find current skill level for this leaf (handle both regular and custom segments)
    const currentLeafSegment = isCustomSegment
      ? employeeData?.employeeLeafSegments?.find(
          segment => segment.customSegment?.id === leafId
        )
      : employeeData?.employeeLeafSegments?.find(
          segment => segment.leafSegment?.id === leafId
        );
    const currentSkillLevel = currentLeafSegment?.competencyLevelId || currentLeafSegment?.competencyLevel?.id || null;

    // If the same skill level is selected, do nothing (but only if not skipping competency level)
    if (!skipCompetencyLevel && competencyLevelId === currentSkillLevel) {
      return;
    }

    const defaultLevelId = getDefaultLevelId(competencyLevels);
    const isRemoving = skipCompetencyLevel
      ? competencyLevelId === defaultLevelId  // When skipping competency, use defaultLevelId to indicate removal
      : competencyLevelId === defaultLevelId;

    try {
      if (isRemoving) {
        // Remove the leaf if skill level is NotProvided
        // Pass customSegmentId for custom segments, leafSegmentId for regular segments
        await employeesApi.removeLeafFromEmployee(
          employeeUser.id,
          isCustomSegment ? null : leafId,
          competencyLevelId,
          isCustomSegment ? leafId : null
        );
        showAlert('Skill unassigned successfully', 'success');

        // Update initialCheckedLeafs by removing the leaf
        setInitialCheckedLeafs(prev => {
          const newSet = new Set(prev);
          newSet.delete(leafId);
          return newSet;
        });
      } else {
        // Add or update the leaf with the selected skill level
        // When skipCompetencyLevel is true, add skill without a competency level
        // Send leafSegmentId OR customSegmentId based on segment type
        await employeesApi.addLeafToEmployee({
          id: employeeUser.id,
          azureId: employeeUser.localAccountId,
          email: employeeUser.username,
          fullName: employeeUser.name,
          ...(isCustomSegment
            ? { customSegmentId: leafId }
            : { leafSegmentId: leafId }
          ),
          ...(!skipCompetencyLevel && competencyLevelId !== null && { competencyLevelId: competencyLevelId })
        });

        showAlert(skipCompetencyLevel ? 'Skill added successfully' : 'Skill level updated successfully', 'success');

        // Update initialCheckedLeafs by adding the leaf
        setInitialCheckedLeafs(prev => new Set([...prev, leafId]));
      }

      // Refresh employee data to get updated skillHistory
      const updatedEmployee = await employeesApi.getEmployeeById(employeeUser.id);
      setEmployeeData(updatedEmployee);

    } catch (error) {
      console.error('Failed to update skill level:', error);
      showAlert('Failed to update skill level', 'error', error);
    }

  };

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);

  // Hooks
  const {
    selectedSubcategory,
    handleSelection,
    handleBack,
  } = useChartData(employeeHierarchyData || {}, TREE_CHART_MODES.TREE);

  const {
    searchTerm,
    searchResults,
    showResults,
    isGlobalSearch,
    handleSearch,
    handleResultSelect,
    setIsGlobalSearch,
    clearSearch
  } = useSearch(filteredData || {}, selectedSubcategory);

  const {
    tooltipData,
    tooltipPosition,
    hoveredItem,
    handleHover,
    handleMouseOut
  } = useTooltip();

  const {
    treeZoomFactor,
    radarZoomFactor,
    panPosition,
    isDragging,
    handleZoomIn,
    handleZoomOut,
    handleResetPosition,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    getZoomFactor,
  } = useZoomPan();

  // Handlers
  const handleExpandClick = useCallback(() => {
    setIsTreeExpanded(!isTreeExpanded);
  }, [isTreeExpanded]);

  const handleNavigationPanelCollapse = useCallback(() => {
    setIsNavigationPanelCollapsed(!isNavigationPanelCollapsed);
  }, [isNavigationPanelCollapsed]);

  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      const isSelected = prev.some(t => t.id === tag.id);
      if (isSelected) {
        return prev.filter(t => t.id !== tag.id);
      }
      return [...prev, tag];
    });
  };

  const handleTagSelectorToggle = useCallback(() => {
    setIsTagSelectorOpen(!isTagSelectorOpen);
  }, [isTagSelectorOpen]);

  // Fetch tags
  useEffect(() => {
    const fetchTags = async () => {
      setIsTagsLoading(true);
      try {
        const tagsData = await tagsApi.getTagHierarchy();
        setTags(tagsData);
      } catch (error) {
        console.error('Failed to load tags:', error);
        showAlert('Failed to load tags', 'error', error);
      } finally {
        setIsTagsLoading(false);
      }
    };

    fetchTags();
  }, [showAlert]);

  const handleOrgConfiguration = () => {
    navigate('/employee-org-configuration', { state: { user: employeeUser } });
  };

  const handleSkillHistoryClick = (nodeData) => {
    setSelectedSkillHistory(nodeData);
  };

  // Handle custom segment creation (segments not in the radar)
  const handleCustomSegmentCreate = async (segmentName) => {
    if (!employeeUser) return;

    try {
      await employeesApi.addLeafToEmployee({
        id: employeeUser.id,
        azureId: employeeUser.localAccountId,
        email: employeeUser.username,
        fullName: employeeUser.name,
        customSegment: { name: segmentName }
      });

      showAlert('Custom segment added successfully', 'success');

      // Clear the search box after successful creation
      clearSearch();

      // Refresh employee data to get updated segments
      const updatedEmployee = await employeesApi.getEmployeeById(employeeUser.id);
      setEmployeeData(updatedEmployee);
    } catch (error) {
      console.error('Failed to add custom segment:', error);
      showAlert('Failed to add custom segment', 'error', error);
    }
  };

  // Loading and error states
  if (loading || radarLoading) {
    return <LoadingSpinner />;
  }

  if (error || radarError) {
    showAlert(error || radarError, 'error', error || radarError);
    return null;
  }

  // If we have an employeeId but no employeeUser yet, show loading while fetching
  if (employeeId && !employeeUser) {
    return <LoadingSpinner message="Loading individual data..." />;
  }

  // If no employeeId and no employeeUser, redirect to home
  if (!employeeId && !employeeUser) {
    return <Navigate to="/" />;
  }


  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => {
            if (fromLandingPage) {
              navigate('/');
            } else if (individualReturn) {
              navigate(individualReturn.path, { state: individualReturn.state });
            } else {
              navigate(`/employees/${employeeUser.id}/edit`);
            }
          }}>
            <FaArrowLeft /> {fromLandingPage ? 'Back to Home' : 'Back to Individual'}
          </BackButton>
          <Title>Technology Configuration | {employeeUser.name}</Title>
        </HeaderLeft>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <HeaderButton onClick={() => navigate(`/employee-radar/${employeeUser.id}/comparison`, { state: { user: employeeUser, fromLandingPage, individualReturn } })}>
            <FaExchangeAlt />
            Technology Sync
          </HeaderButton>
          <HeaderButton onClick={handleOrgConfiguration}>
            <FaUserCog />
            Configure
          </HeaderButton>
          <HeaderButton onClick={() => setIsPanelsVisible(!isPanelsVisible)}>
            {isPanelsVisible ? <FaEyeSlash /> : <FaEye />}
            {isPanelsVisible ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </div>
      </Header>

      <Content style={{ 
        marginTop: isPanelsVisible ? '0' : '0',
        gap: isPanelsVisible ? '12px' : '0' 
      }}>
        <NavigationPanel
          isExpanded={!isNavigationPanelCollapsed}
          onToggle={handleNavigationPanelCollapse}
          searchBar={
            <div style={{ flex: 1 }}>
              <SearchBar
                searchTerm={searchTerm}
                searchResults={searchResults}
                showResults={showResults}
                isGlobalSearch={isGlobalSearch}
                selectedSubcategory={selectedSubcategory}
                onSearch={handleSearch}
                onResultSelect={(result) => {
                  const { selectedNode, pathToNode } = handleResultSelect(result);
                  setExpandedPath(pathToNode);
                  handleSelection(selectedNode);
                }}
                onToggleSearchMode={() => setIsGlobalSearch(!isGlobalSearch)}
                allowCustomSegment={true}
                onCustomSegmentCreate={handleCustomSegmentCreate}
              />
            </div>
          }
          breadcrumb={
            <Breadcrumb
              data={filteredData || {}}
              selectedSubcategory={selectedSubcategory}
              onNavigate={handleSelection}
            />
          }
          isVisible={isPanelsVisible}
          onTagSelect={handleTagSelectorToggle}
          selectedTagsCount={selectedTags.length}
          configMode={TREE_CHART_MODES.EMPLOYEE}
          chartType={TREE_CHART_MODES.TREE}
          selectedCount={initialCheckedLeafs.size}
          showOnlySelected={showOnlySelected}
          onToggleShowSelected={() => setShowOnlySelected(!showOnlySelected)}
        />

        <ChartPanel>
          <div style={{ display: 'flex', gap: '1rem', height: '100%' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <TreeChart
                data={filteredData}
                selectedSubcategory={selectedSubcategory}
                projectData={null}
                hoveredItem={hoveredItem}
                onHover={() => {}}
                onMouseOut={() => {}}
                onSelection={handleSelection}
                onBack={handleBack}
                zoomFactor={treeZoomFactor}
                configMode={TREE_CHART_MODES.EMPLOYEE}
                expandedPath={expandedPath}
                setExpandedPath={setExpandedPath}
                isLeafAssigned={isLeafAssigned}
                initialCheckedLeafs={initialCheckedLeafs}
                onLeafToggle={handleLeafToggle}
                onSkillLevelChange={handleSkillLevelChange}
                onSkillHistoryClick={handleSkillHistoryClick}
                isTreeExpanded={isTreeExpanded}
                onTreeExpansionToggle={setIsTreeExpanded}
                skillLevels={competencyLevels}
              />
              <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '1rem' }}>
                <ZoomControls
                  zoomFactor={treeZoomFactor}
                  onZoomIn={(isMax) => handleZoomIn(TREE_CHART_MODES.TREE, isMax)}
                  onZoomOut={() => handleZoomOut(TREE_CHART_MODES.TREE)}
                  onReset={() => handleResetPosition(TREE_CHART_MODES.TREE)}
                />
              </div>
            </div>
          </div>
        </ChartPanel>

        {isTagSelectorOpen && (
          <TagSelectorModalComponent
            isOpen={isTagSelectorOpen}
            onClose={() => setIsTagSelectorOpen(false)}
            tags={tags}
            selectedTags={selectedTags}
            onTagToggle={handleTagToggle}
          />
        )}

        <SkillHistoryModal
          isOpen={!!selectedSkillHistory}
          onClose={() => setSelectedSkillHistory(null)}
          skillData={selectedSkillHistory}
          skillLevels={competencyLevels.length > 0 ? competencyLevels : []}
        />
      </Content>
    </Container>
  );
};

export default EmployeeConfiguration; 